import * as React from 'react';

import { connect } from 'react-redux';
import { Button, Checkbox, Icon } from 'semantic-ui-react';

import { localeData } from '../../../locales/data';
import { StateType } from '../../../reducers';
import { activeTabSelector, getCurrentCode } from '../../../reducers/EditorReducer/EditorSelector';
import { dataSelector } from '../../../reducers/SimulationReducer/DataReducer/DataSelector';
import { filtersSelector } from '../../../reducers/SimulationReducer/FiltersReducer/FiltersSelector';
import { addModifiers } from '../../../shared/helpers';
import { isMobile } from '../../../shared/helpers/responsive';
import { ToolTip } from '../../ToolTip/';

import './EditorBottomBarLeft.scss';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import moment from 'moment';

interface EditorBottomBarLeftProps {
  handleSimulate: () => void;
  isMultiTab: boolean;
  handleChangeVisualization: () => void;
  visualization: boolean;
  isPythonCode: boolean;
  showExample?: boolean;
  isFetching: boolean;
  handleExample: () => void;
  isConsultant: boolean;
  isVisualization: boolean;
  emptyCode: boolean;
  currentActiveTab: number;
  currentChildActiveTab: number;
  tabs: any;
  data: any;
  path: any;
  isFetchingCheck: boolean;
  simulationType: string;
  handleCloneAlpha: () => void;
}

interface EditorBottomBarLeftStateProps {
  filters: any;
  fetchingRecordSets: boolean;
  fetchingData: boolean;
  allData: any;
  currentTab: any;
  currentCode: string;
  activeTab: any;
  currentCombo: string;
  currentSelection: string;
}

export class EditorBottomBarLeft extends React.Component<EditorBottomBarLeftProps & EditorBottomBarLeftStateProps> {
  state = { isMobile: isMobile('mobile') };

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateMobileState);
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateMobileState);
  }

  updateMobileState = () => {
    this.setState({ isMobile: isMobile('mobile') });
  };

  showCode = () =>
    this.props.filters.getIn([this.props.currentActiveTab, 'code']) || this.props.filters.getIn([this.props.currentActiveTab, 'superCode']);

  settingsMatch(tabSettings: any, dataSettings: any) {
    let sameSettings = true;
    tabSettings.forEach((_: any, setting: any) => {
      const tabValue = tabSettings.getIn([setting, 'value']);
      const dataValue = dataSettings.getIn([setting]);
      if (tabValue && dataValue && tabValue.toString() !== dataValue.toString()) {
        sameSettings = false;
      } else if (setting === 'decay' && (Number(dataValue) || 0) !== (Number(tabValue) || 0)) {
        sameSettings = false;
      } else if (
        setting === 'testPeriod' &&
        moment.duration(dataValue || 0).asMilliseconds() !== moment.duration(tabValue || 0).asMilliseconds()
      ) {
        sameSettings = false;
      }
    });

    return sameSettings;
  }

  checkSameCode() {
    const { data, currentActiveTab, isMultiTab } = this.props;

    if (isMultiTab) {
      let childTabWithSameCode = true;
      const childTabs = this.props.tabs.getIn([currentActiveTab, 'childTabs']);
      const childData = this.props.allData.getIn([currentActiveTab, 'children']);
      if (childTabs && childData) {
        childTabs.forEach((tab: any, index: number) => {
          if (tab.get('code') !== childData.getIn([index, 'alphaDetail', 'regular', 'code'])) {
            childTabWithSameCode = false;
          }
        });
      }

      return childTabWithSameCode;
    } else if (this.props.tabs.getIn([currentActiveTab, 'superAlpha'])) {
      return (
        this.props.tabs.getIn([currentActiveTab, 'combo']) === data.getIn(['combo', 'code']) &&
        this.props.tabs.getIn([currentActiveTab, 'selection']) === data.getIn(['selection', 'code'])
      );
    } else {
      return this.props.tabs.getIn([currentActiveTab, 'code']) === data.getIn(['regular', 'code']);
    }
  }

  checkIsFetching() {
    const { currentTab, isMultiTab, currentChildActiveTab, fetchingRecordSets, fetchingData, isFetchingCheck } = this.props;

    if (isMultiTab) {
      const tab = currentTab ? currentTab.getIn(['children', currentChildActiveTab]) : null;
      const tabFetchingRecordSets = tab ? tab.get('fetchingRecordSets') : false;
      const tabFetchingRecordSetsChart = tab ? tab.get('fetchingRecordSetsChart') : false;

      return isFetchingCheck || tabFetchingRecordSets || tabFetchingRecordSetsChart || fetchingData;
    } else {
      return isFetchingCheck || fetchingRecordSets || fetchingData;
    }
  }

  shouldSimulate() {
    const { data, isFetchingCheck, emptyCode, currentActiveTab, isMultiTab, currentChildActiveTab } = this.props;
    let shouldSimulate = false;
    // If data !== null the active tab was simulated
    if (data) {
      const activeTab = isMultiTab
        ? this.props.tabs.getIn([currentActiveTab, 'childTabs', currentChildActiveTab])
        : this.props.tabs.get(currentActiveTab);

      if (activeTab) {
        const tabSettings = activeTab.get('settings');
        const parentSettings = isMultiTab ? this.props.tabs.getIn([currentActiveTab, 'settings']) : null;
        const sameCode = this.checkSameCode();
        if ((!sameCode || !this.settingsMatch(tabSettings.merge(parentSettings), data.get('settings'))) && !emptyCode && !isFetchingCheck) {
          shouldSimulate = isMultiTab && this.state.isMobile ? false : true;
        }
      }
    } else {
      shouldSimulate = emptyCode || this.checkIsFetching() || (isMultiTab && this.state.isMobile) ? false : true;
    }

    return shouldSimulate;
  }

  shouldDisableClone() {
    const { currentCode, currentCombo, currentSelection, simulationType, activeTab } = this.props;
    const disbaleSuper = !(currentCombo && currentCombo.trim().length > 0 && currentSelection && currentSelection.trim().length > 0);
    const disbaleRegular = !(currentCode && currentCode.trim().length > 0) || activeTab.get('childTabs');

    if (activeTab.get('childTabs') && activeTab.get('childTabs').size > 9) {
      return true;
    } else {
      return simulationType === 'super' ? disbaleSuper : disbaleRegular;
    }
  }

  render() {
    const {
      handleSimulate,
      isMultiTab,
      handleChangeVisualization,
      visualization,
      isPythonCode,
      showExample,
      handleExample,
      isConsultant,
      isVisualization,
      emptyCode,
      isFetchingCheck,
      handleCloneAlpha,
    } = this.props;
    let modifiers: string[] = [];
    modifiers = !this.shouldSimulate() ? modifiers.concat(['disabled']) : modifiers;
    modifiers = isMultiTab ? modifiers.concat(['multi']) : modifiers;
    modifiers = isPythonCode ? modifiers.concat(['python']) : modifiers;
    modifiers = isConsultant ? modifiers.concat(['isConsultant']) : modifiers;
    modifiers = emptyCode || !isFetchingCheck ? modifiers : modifiers.concat(['disabled-example']);
    modifiers = this.showCode() ? modifiers.concat(['is-code']) : modifiers;

    return (
      <div className={addModifiers('editor-bottom-bar-left-monaco', modifiers)}>
        <div className="editor-button-container">
          {showExample && (
            <button
              className={`intro-step-4 editor-button--example ${addModifiers('editor-button', modifiers)}`}
              disabled={modifiers.indexOf('disabled-example') > -1}
              onClick={handleExample}
            >
              <Icon name="lightbulb outline" />
              <span className="editor-button__text">
                <FormattedMessageHelper
                  tagName="span"
                  id="editorBottomBarLeft.example"
                  defaultMessage={localeData.en['editorBottomBarLeft.example']}
                />
              </span>
            </button>
          )}

          {isMobile('mobile') && (
            <Button disabled={Boolean(this.shouldDisableClone())} className="editor-button editor-button-clone" onClick={handleCloneAlpha}>
              <span className="editor-tabs__new-tab-text">
                <FormattedMessageHelper
                  tagName="span"
                  id="codeEditor.cloneThisAlpha.mobile"
                  defaultMessage={localeData.en['codeEditor.cloneThisAlpha.mobile']}
                />
              </span>
            </Button>
          )}
        </div>
        <div className={addModifiers('editor-simulate', modifiers)}>
          <div className={addModifiers('editor-simulate-button', modifiers)}>
            <button
              className={addModifiers('intro-step-5 editor-simulate-button-text', modifiers)}
              disabled={modifiers.indexOf('disabled') > -1}
              onClick={handleSimulate}
            >
              {isMultiTab ? (
                <FormattedMessageHelper
                  tagName="span"
                  id="editorBottomBarLeft.multiSimulate"
                  defaultMessage={localeData.en['editorBottomBarLeft.multiSimulate']}
                />
              ) : (
                <FormattedMessageHelper
                  tagName="span"
                  id="editorBottomBarLeft.simulate"
                  defaultMessage={localeData.en['editorBottomBarLeft.simulate']}
                />
              )}
            </button>
            {isVisualization && !this.state.isMobile && (
              <div className={addModifiers('editor-simulate-checkbox-label', modifiers)}>
                <Checkbox
                  className="editor-button__checkbox"
                  onClick={isFetchingCheck || emptyCode ? undefined : handleChangeVisualization}
                  checked={visualization}
                  disabled={emptyCode ? true : false}
                />
                <div className="editor-button__checkbox-label">
                  <FormattedMessageHelper
                    tagName="span"
                    id="editorBottomBarLeft.visualization"
                    defaultMessage={localeData.en['editorBottomBarLeft.visualization']}
                  />
                  <ToolTip label="editoBottomBarLeft.bluePopUp.visualization" tooltipInfo="editoBottomBarLeft.bluePopUp.toolTip" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: StateType, ownProps: EditorBottomBarLeftProps): EditorBottomBarLeftStateProps => ({
  filters: filtersSelector(state),
  allData: dataSelector(state),
  // @ts-ignore
  fetchingRecordSets: state.getIn(['simulation', 'data', ownProps.currentActiveTab, 'fetchingRecordSets']),
  // @ts-ignore
  fetchingData: state.getIn(['simulation', 'data', ownProps.currentActiveTab, 'fetchingData']),
  currentTab: state.getIn(['simulation', 'data', ownProps.currentActiveTab]),
  activeTab: activeTabSelector(state),
  currentCode: getCurrentCode(state, 'code'),
  currentCombo: getCurrentCode(state, 'combo'),
  currentSelection: getCurrentCode(state, 'selection'),
});

export default connect<EditorBottomBarLeftStateProps, any, any, StateType>(mapStateToProps)(EditorBottomBarLeft);
